// Turning a Wikipedia paragraph into a way into the library.
//
// Two kinds of thing in that prose are worth clicking, and the app can honour
// exactly those two: a TUNE we hold (open it) and a PERSON the library credits
// (search for their tunes). Everything else stays plain text — a link that
// leads nowhere is worse than no link.
//
// Both rules are evidence-first, in the spirit of the manifest that chose these
// articles in the first place:
//
//   Tunes  Wikipedia quotes song titles ('"Blue Bossa" is a jazz standard',
//          Monk's lead names six of his in quotes) and italicises albums, which
//          the plain-text extract drops — so a QUOTED phrase is a strong signal
//          of "song", and the phrase must also match a tune we can open.
//          Quotes wrap other things too (nicknames — "Bird", "Kenny" — and
//          ordinary quotation), which the library match filters out.
//
//   People Only full names of at least two words, and only names the library
//          credits, so the search behind the link is guaranteed to land on
//          something. A bare surname would match half the prose in the corpus.
//
// First mention only, like an encyclopaedia: the fifth "Charlie Parker" in a
// paragraph is not a fifth invitation to search for him.

import { normalizeTuneText } from '@/music/tuneRelations';

export type ProseSegment =
  | { kind: 'text'; text: string }
  | { kind: 'tune'; text: string; title: string; composer: string }
  | { kind: 'person'; text: string; name: string };

export interface ProseIndex {
  /** Normalised title → the tune to open. */
  tunes: Map<string, { title: string; composer: string }>;
  /** Normalised full name → the credit as the library spells it. */
  people: Map<string, string>;
}

/**
 * A co-written credit names two writers, and the prose may mention either.
 * Split on the library's separators but keep the WRITTEN name: it has to match
 * the text of an article, not our normal form.
 */
function creditNames(composer: string | undefined): string[] {
  return (composer ?? '')
    .split(/[,&/]|\band\b/i)
    .map((name) => name.trim())
    .filter((name) => name.split(/\s+/).length >= 2);
}

export function buildProseIndex(tunes: { title: string; composer: string }[]): ProseIndex {
  const index: ProseIndex = { tunes: new Map(), people: new Map() };
  for (const tune of tunes) {
    const key = normalizeTuneText(tune.title);
    // First writer wins a shared title: the panel needs one destination, and a
    // second "Yesterdays" is a different tune we cannot choose between.
    if (key && !index.tunes.has(key)) index.tunes.set(key, tune);
    for (const name of creditNames(tune.composer)) {
      const nameKey = normalizeTuneText(name);
      if (nameKey && !index.people.has(nameKey)) index.people.set(nameKey, name);
    }
  }
  return index;
}

interface Hit {
  start: number;
  end: number;
  segment: ProseSegment;
}

/** Runs of capitalised words — the shape a person's name takes in a sentence. */
const NAME_RUN = /\b[A-Z][\p{L}'’.-]*(?:\s+[A-Z][\p{L}'’.-]*){1,3}/gu;
const QUOTED = /["“]([^"”]{2,60})["”]/g;

/**
 * A name in a sentence wears the sentence's punctuation: "Joe Henderson's
 * album", "played with Kenny Dorham." Neither belongs to the person, and both
 * stop the match — the apostrophe survives normalisation as a word break.
 * Initials keep their dots ("J.J. Johnson"); only trailing ones go.
 */
function trimName(value: string): string {
  for (let out = value; ; ) {
    const next = out.replace(/['’]s$/u, '').replace(/[.,;:!?)"”'’-]+$/u, '');
    if (next === out) return out;
    out = next;
  }
}

/**
 * `skip` is what the panel already gives you — the tune you have open and the
 * writers whose cards are right there. Linking those is noise pointing at the
 * screen you are on.
 */
export function linkifyProse(text: string, index: ProseIndex, skip: string[] = []): ProseSegment[] {
  const skipped = new Set(skip.map((value) => normalizeTuneText(value)).filter(Boolean));
  const taken = new Set<string>();
  const hits: Hit[] = [];

  for (const match of text.matchAll(QUOTED)) {
    const inner = match[1];
    const key = normalizeTuneText(inner);
    const tune = index.tunes.get(key);
    if (!tune || skipped.has(key) || taken.has(`tune:${key}`)) continue;
    taken.add(`tune:${key}`);
    // The quotes stay in the sentence; only the title is a link.
    const start = (match.index ?? 0) + match[0].indexOf(inner);
    hits.push({
      start,
      end: start + inner.length,
      segment: { kind: 'tune', text: inner, title: tune.title, composer: tune.composer },
    });
  }

  for (const match of text.matchAll(NAME_RUN)) {
    const run = match[0];
    const runStart = match.index ?? 0;
    const words = run.split(/\s+/);
    // Longest first: "Billy Eckstine" inside "Dizzy Gillespie and Billy
    // Eckstine" comes out of the two-word windows, but a three-word name
    // ("Ruby My Dear" is not a person, "Jimmy Van Heusen" is) must win whole.
    for (let size = Math.min(4, words.length); size >= 2; size -= 1) {
      for (let at = 0; at + size <= words.length; at += 1) {
        const name = trimName(words.slice(at, at + size).join(' '));
        const key = normalizeTuneText(name);
        const credit = index.people.get(key);
        if (!credit || skipped.has(key) || taken.has(`person:${key}`)) continue;
        const offset = words.slice(0, at).join(' ').length + (at > 0 ? 1 : 0);
        const start = runStart + offset;
        if (hits.some((hit) => start < hit.end && start + name.length > hit.start)) continue;
        taken.add(`person:${key}`);
        hits.push({
          start,
          end: start + name.length,
          segment: { kind: 'person', text: name, name: credit },
        });
      }
    }
  }

  hits.sort((a, b) => a.start - b.start);
  const out: ProseSegment[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start < cursor) continue;
    if (hit.start > cursor) out.push({ kind: 'text', text: text.slice(cursor, hit.start) });
    out.push(hit.segment);
    cursor = hit.end;
  }
  if (cursor < text.length) out.push({ kind: 'text', text: text.slice(cursor) });
  return out;
}
